import { Button, Card, Icon, Tooltip } from "pepsico-ds";
import PropTypes from "prop-types";
import { getDateWithFormat } from "../../utils/commons";
import {
  CODE_GROUP_CATEGORY,
  CODE_GROUP_LIFE_CYCLE_STATUS,
} from "../../utils/constants";
import { FeatureToggle, useFeatureToggle } from "../../utils/featureToggle";
import { codeGroupActionButton } from "./codeGroupActionButton";
import "./codeGroupsCard.scss";

const CodeGroupsCard = ({
  group,
  currentProgramTimezone,
  downloadPermission,
  handleActionButtons,
  isSelected,
  onSelect,
}) => {
  const ptrCodeGroupUpload = useFeatureToggle(FeatureToggle.ptrCodeGroupUpload);
  const hasMultiFileDownloadEnabled =
    useFeatureToggle(FeatureToggle.enableMultiFileDownload) || false;

  if (!group) {
    return "No Data Found";
  }
  const allowedStatuses = [
    CODE_GROUP_LIFE_CYCLE_STATUS.DRAFT.toLowerCase(),
    CODE_GROUP_LIFE_CYCLE_STATUS.INACTIVE.toLowerCase(),
  ];
  const basicSetup = group.basicSetup || {};
  const producerPackaging = group.producerPackaging || {};
  const producerCompany = producerPackaging.producerCompany || "-";
  const creationType = group.creationType || "-";
  const codesGeneratedCount =
    group.codesGeneratedCount !== undefined && group.codesGeneratedCount !== null
      ? group.codesGeneratedCount
      : "-";
  const codesUsedCount =
    group.codesUsedCount !== undefined && group.codesUsedCount !== null
      ? group.codesUsedCount
      : "-";

  const compositeStatus = group.compositeStatus || "-";

  const codeGroupName = basicSetup.codeGroupName || "-";
  const codeActiveStartDate = basicSetup.codeActiveStartDate || null;
  const codeActiveEndDate = basicSetup.codeActiveEndDate || null;
  const rawCategory = group.category || CODE_GROUP_CATEGORY.DEFAULT;
  const category =
    rawCategory?.toUpperCase() === CODE_GROUP_CATEGORY.SUPPORT
      ? "Support"
      : "Default";
  const canSelect = allowedStatuses.includes(group.compositeStatus?.toLowerCase());

  const formattedStartDate = codeActiveStartDate
    ? getDateWithFormat(codeActiveStartDate, currentProgramTimezone)
    : "-";
  const formattedEndDate = codeActiveEndDate
    ? getDateWithFormat(codeActiveEndDate, currentProgramTimezone)
    : "-";
  const formattedDateRange =
    [formattedStartDate, formattedEndDate].filter((date) => date !== "-").length > 0
      ? [formattedStartDate, formattedEndDate]
          .filter((date) => date !== "-")
          .join(" - ")
      : "-";
  const formattedCodeCount =
    typeof codesGeneratedCount === "number"
      ? codesGeneratedCount.toLocaleString()
      : codesGeneratedCount;
  const orderedActions = ["view", "edit", "copy", "export"];
  const cardActions = codeGroupActionButton(
    group,
    handleActionButtons,
    downloadPermission,
    ptrCodeGroupUpload,
    true
  )
    .filter((action) => orderedActions.includes(action.action))
    .sort(
      (a, b) => orderedActions.indexOf(a.action) - orderedActions.indexOf(b.action)
    );
  const normalizedCategory = category.toLowerCase().replace(/\s/g, "-");
  const normalizedStatus = compositeStatus
    ? compositeStatus.toLowerCase().replace(/\s/g, "-")
    : "unknown";

  const isFileReadyToDownload = group?.exportFile?.length > 0;

  return (
    <Card
      icon=""
      ellipsis=""
      style={{
        padding: "0px",
      }}
      className={`code-group-card ${isSelected ? "selected" : ""}`}
    >
      <div className="code-group-card-container">
        <div className="code-group-card-details">
          <div className="code-group-header">
            {canSelect ? (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelect(group.codeGroupId, e.target.checked)}
              />
            ) : null}
            <div className="code-group-title">
              <h3 className="code-group-name">{codeGroupName}</h3>
            </div>
            <div className="code-group-badges">
              <span className={`code-category-badge ${normalizedCategory}`}>
                {category}
              </span>
              <span className={`code-status-badge ${normalizedStatus}`}>
                {compositeStatus}
              </span>
            </div>
          </div>
          <div className="code-group-separator" />
          <div className="code-group-info">
            {!ptrCodeGroupUpload ? (
              <div className="code-group-info-grid">
                <div className="info-item">
                  <span className="info-label">
                    Unique Codes
                    <Tooltip
                      direction="right"
                      text="Codes might have limited or unlimited uses"
                    >
                      <Icon icon="info" alt="" size="small" />
                    </Tooltip>
                  </span>
                  <span className="info-value">{formattedCodeCount}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Active Start and End Date</span>
                  <span className="info-value">{formattedDateRange}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Redemptions Used</span>
                  <span className="info-value">
                    {typeof codesUsedCount === "number"
                      ? codesUsedCount.toLocaleString()
                      : codesUsedCount}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Producer Company</span>
                  <span className="info-value">{producerCompany}</span>
                </div>
              </div>
            ) : (
              <div className="code-group-info-grid">
                <div className="info-item">
                  <span className="info-label">
                    Unique Codes
                    <Tooltip
                      direction="right"
                      text="Codes might have limited or unlimited uses"
                    >
                      <Icon icon="info" alt="" size="small" />
                    </Tooltip>
                  </span>
                  <span className="info-value">{formattedCodeCount}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Creation Type</span>
                  <span className="info-value">{creationType}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Redemptions Used</span>
                  <span className="info-value">
                    {typeof codesUsedCount === "number"
                      ? codesUsedCount.toLocaleString()
                      : codesUsedCount}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Start Date</span>
                  <span className="info-value">{formattedStartDate}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">End Date</span>
                  <span className="info-value">{formattedEndDate}</span>
                </div>
              </div>
            )}
          </div>
          <div className="code-group-separator" />
          <div className="code-group-actions">
            {cardActions.map((button) =>
              hasMultiFileDownloadEnabled && button.action === "export" ? (
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => handleActionButtons("downloadMultipleFile", group)}
                  key={button.action}
                >
                  Download
                </Button>
              ) : (
                <button
                  key={button.action}
                  type="button"
                  className={`code-group-action-btn ${
                    button.action === "export" ? "primary" : ""
                  }`}
                  disabled={button.action === "export" && !isFileReadyToDownload}
                  onClick={button.onClick}
                >
                  {button.action === "export"
                    ? "Download"
                    : button.label || button.action}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

CodeGroupsCard.propTypes = {
  group: PropTypes.shape({
    basicSetup: PropTypes.shape({
      codeGroupName: PropTypes.string,
      codeActiveStartDate: PropTypes.string,
      codeActiveEndDate: PropTypes.string,
    }),
    producerPackaging: PropTypes.shape({
      producerCompany: PropTypes.string,
    }),
    codesGeneratedCount: PropTypes.number,
    administrationStatus: PropTypes.string,
    compositeStatus: PropTypes.string,
    category: PropTypes.string,
    sampleCode: PropTypes.string,
    codeSample: PropTypes.string,
    creationType: PropTypes.string,
    codeSpecifications: PropTypes.shape({
      codeLength: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      excludeCharacters: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      excludeCharacterValues: PropTypes.arrayOf(PropTypes.string),
      sampleCode: PropTypes.string,
    }),
  }),
  currentProgramTimezone: PropTypes.string,
};

export default CodeGroupsCard;
